import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row, Table, Form, Pagination } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PageTitle from "../../Components/PageTitle";
import Preloader from "../../Components/Preloader";
import { logout } from "../../redux/actions/AuthActions";
import { getUserWalletDetails } from "../../redux/actions/MasterActions";
import { getDisplayDateTimeFormat } from "../../util/generalUtills";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }
  return currencyFormatter.format(numericValue);
};

const formatTime = (value) => {
  if (!value) {
    return "--";
  }
  try {
    return getDisplayDateTimeFormat(value);
  } catch (error) {
    return value;
  }
};

const safeText = (value, fallback = "--") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  if (typeof value === "object") {
    if (typeof value.name === "string" && value.name.trim().length > 0) {
      return value.name;
    }
    return fallback;
  }
  const text = String(value);
  return text.trim().length > 0 ? text : fallback;
};

const UserWalletDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();

  const {
    AuthReducer: { auth },
    masterReducer: {
      loadingMaster,
      userWalletDetails,
      userWalletCurrentBalance,
      errorsMaster,
    },
  } = useSelector((state) => state);

  const fallbackUser = location.state?.userName
    ? {
        name: location.state?.userName,
        designation: location.state?.designation,
        phone: location.state?.phone,
      }
    : null;
  useEffect(() => {
    if (auth?.token) {
      dispatch(getUserWalletDetails(auth.token, userId));
    }
  }, [auth?.token, userId, dispatch]);

  useEffect(() => {
    if (errorsMaster && Object.keys(errorsMaster).length > 0 && errorsMaster?.status === 401) {
      dispatch(logout());
      toast.error(errorsMaster?.data?.message || "Your session has expired.");
    }
  }, [errorsMaster, dispatch]);

  const walletDetails = useMemo(
    () => (Array.isArray(userWalletDetails) ? userWalletDetails : []),
    [userWalletDetails]
  );

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const extractEntryDate = (entry) => {
    const dateValue =
      entry?.time ??
      entry?.created_at ??
      entry?.createdAt ??
      entry?.updated_at ??
      entry?.updatedAt ??
      entry?.date;
    if (!dateValue) return null;
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const filteredWalletDetails = useMemo(() => {
    if (!Array.isArray(walletDetails)) return [];

    const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
    const end = endDate ? new Date(`${endDate}T23:59:59`) : null;

    return walletDetails.filter((entry) => {
      const entryDate = extractEntryDate(entry);
      if ((start || end) && !entryDate) {
        return false;
      }
      if (start && entryDate < start) {
        return false;
      }
      if (end && entryDate > end) {
        return false;
      }
      return true;
    });
  }, [walletDetails, startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, walletDetails]);

  const totalPages = useMemo(() => {
    const pages = Math.ceil(filteredWalletDetails.length / itemsPerPage);
    return pages > 0 ? pages : 1;
  }, [filteredWalletDetails.length, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedWalletDetails = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredWalletDetails.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredWalletDetails, currentPage, itemsPerPage]);

  const totalCredit = useMemo(() => {
    return filteredWalletDetails.reduce((sum, entry) => {
      const rawValue =
        entry?.credit ??
        entry?.credit_amount ??
        entry?.creditAmount ??
        entry?.fee_amount ??
        entry?.feeAmount ??
        entry?.amount;
      const numericValue = Number(rawValue);
      return Number.isNaN(numericValue) ? sum : sum + numericValue;
    }, 0);
  }, [filteredWalletDetails]);

  const totalDebit = useMemo(() => {
    return filteredWalletDetails.reduce((sum, entry) => {
      const rawValue = entry?.debit ?? entry?.debit_amount ?? entry?.debitAmount;
      const numericValue = Number(rawValue);
      return Number.isNaN(numericValue) ? sum : sum + numericValue;
    }, 0);
  }, [filteredWalletDetails]);

  const primaryLog = Array.isArray(userWalletDetails) && userWalletDetails.length > 0
    ? userWalletDetails[0]
    : null;

  const resolvedDisplay = primaryLog?.user_idWalletLog
    ? {
        name: primaryLog.user_idWalletLog.name ?? "--",
        designation: location.state?.designation ?? "--",
        contact: primaryLog.user_idWalletLog.phone ?? "--",
      }
    : { name: "--", designation: "--", contact: "--", wallet_balance: "--" };
  const fallbackDisplay = fallbackUser
    ? {
        name: fallbackUser?.name ?? "--",
        designation: fallbackUser?.designation ?? "--",
        contact: fallbackUser?.phone ?? "--",
      }
    : null;

  const userBalanceRaw =
    typeof userWalletCurrentBalance === "number"
      ? userWalletCurrentBalance
      : primaryLog?.closingBal ?? null;

  const userDisplayInfo = {
    name: resolvedDisplay?.name ?? fallbackDisplay?.name ?? "--",
    designation: resolvedDisplay?.designation ?? fallbackDisplay?.designation ?? "--",
    contact: resolvedDisplay?.contact ?? fallbackDisplay?.contact ?? "--",
    balance: formatAmount(userBalanceRaw),
  };

  // useEffect(() => {
  //   if (!loadingMaster && Array.isArray(userWalletDetails) && userWalletDetails.length === 0) {
  //     toast.warning("No wallet data found for the selected user.");
  //   }
  // }, [loadingMaster, userWalletDetails]);

  return (
    <>

<Row className="mt-3">
        <Col>
          <Button variant="primary" onClick={() => navigate(-1)}>
            <i className="mdi mdi-arrow-left me-1"></i>
            {t("Back")}
          </Button>
        </Col>
      </Row>


      <PageTitle
        breadCrumbItems={[
          { label: t("Attendance"), path: "/Attendance/Daily", active: false },
          { label: t("User Wallet"), path: "/Attendance/User-Wallet", active: false },
          { label: t("Wallet Details"), path: "#", active: true },
        ]}
        title={t("User Wallet Details")}
      />

      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Body>
              <Row className="gy-2">
                <Col lg={3}>
                  <div className="text-muted">{t("Employee Name")}</div>
                  <div className="fw-semibold text-dark">{userDisplayInfo.name}</div>
                </Col>
                <Col lg={3}>
                  <div className="text-muted">{t("Designation")}</div>
                  <div className="fw-semibold text-dark">{userDisplayInfo.designation}</div>
                </Col>
                <Col lg={3}>
                  <div className="text-muted">{t("Contact")}</div>
                  <div className="fw-semibold text-dark">{userDisplayInfo.contact}</div>
                </Col>
                <Col lg={3}>
                  <div className="text-muted">{t("Current Balance")}</div>
                  <div className="fw-semibold text-dark">{userDisplayInfo.balance}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Row className="g-3 mb-3">
                <Col lg={3} md={4} sm={6}>
                  <Form.Group controlId="walletStartDate">
                    <Form.Label className="text-muted small mb-1">{t("Start Date")}</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(event) => setStartDate(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col lg={3} md={4} sm={6}>
                  <Form.Group controlId="walletEndDate">
                    <Form.Label className="text-muted small mb-1">{t("End Date")}</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(event) => setEndDate(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col lg="auto" className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    {t("Clear")}
                  </Button>
                </Col>
              </Row>

              <div className="table-responsive" style={{ maxHeight: "480px" }}>
                <Table
                  hover
                  striped
                  bordered
                  className="align-middle mb-0"
                  size="lg"
                >
                  <thead
                    className="table-light"
                    style={{ position: "sticky", top: 0, zIndex: 1 }}
                  >
                    <tr>
                      <th className="text-uppercase small text-muted fw-medium text-center">Remark</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Case/Trip</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Claim Type</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Credit</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Debit</th>
                      <th className="text-uppercase small text-muted fw-medium text-center">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingMaster && (
                      <tr>
                        <td colSpan={6}>
                          <Preloader />
                        </td>
                      </tr>
                    )}
                    {!loadingMaster && filteredWalletDetails.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {t("No wallet history available for this user.")}
                        </td>
                      </tr>
                    )}
                    {!loadingMaster &&
                      paginatedWalletDetails.map((entry, index) => (
                        <tr key={entry?.id ?? index}>
                          <td className="text-wrap text-center">{safeText(entry?.remark ?? entry?.remarks)}</td>
                          <td className="text-wrap text-center">
                            {(() => {
                              if(entry?.case_id){
                                return (
                                  <Link 
                                    to={`/Case-Summary/${entry?.case_id}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(window.location.origin + `/Case-Summary/${entry?.case_id}`, '_blank', 'noopener,noreferrer');
                                    }}
                                    style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                                  >
                                    {safeText(entry?.claimTypeWalletLog?.name)}
                                  </Link>
                                );
                              }
                              if(entry?.trip_id){
                                return (
                                  <Link 
                                    to={`/Trip-Report`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(window.location.origin + `/Trip-Report/${entry?.user_id}`, '_blank', 'noopener,noreferrer');
                                    }}
                                    style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                                  >
                                    {safeText('Trip Details')}
                                  </Link>
                                );
                              }
                              return "--";
                            })()}
                          </td>
                          <td className="text-wrap text-center">
                            {safeText(entry?.claim_id)}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-soft-success text-success px-3 py-2">
                              {formatAmount(
                                entry?.transction_type === "Credit" ? entry?.amount : 0
                              )}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-soft-danger text-danger px-3 py-2">
                              {formatAmount(entry?.debit ?? entry?.debit_amount ?? entry?.debitAmount)}
                            </span>
                          </td>
                          <td className="text-center">
                            {formatTime(
                              entry?.createdAt ?? entry?.updatedAt
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
                <div className="border-top pt-3 mt-3 d-flex justify-content-end gap-3">
                  <div className="d-flex flex-column text-end">
                    <span className="text-muted text-uppercase small">{t("Total Credit")}</span>
                    <span className="fw-semibold text-success">
                      {formatAmount(totalCredit)}
                    </span>
                  </div>
                  <div className="d-flex flex-column text-end">
                    <span className="text-muted text-uppercase small">{t("Total Debit")}</span>
                    <span className="fw-semibold text-danger">
                      {formatAmount(totalDebit)}
                    </span>
                  </div>
                </div>

                {filteredWalletDetails.length > itemsPerPage && (
                  <Pagination className="justify-content-end mt-3">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    />
                    {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNumber) => (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                    />
                  </Pagination>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default UserWalletDetail;

